









































import { Either, left, Left } from 'fp-ts/lib/Either'
import Vue from 'vue'
import InfiniteLoading, { StateChanger } from 'vue-infinite-loading'
import { Component, Emit, Prop, Watch } from 'vue-property-decorator'

import { ListWithPagination, Pagination } from '@/models/api/response/list'
import { LoadedPage, LoadingBlankPage } from '@/models/page'
import { PageCollection } from '@/models/page/page-collection'
import { AutoLoadComplete, AutoLoadError, FetchCallback } from './model'

@Component({
  components: {
    InfiniteLoading,
  },
})
export default class AutoLoadContainer extends Vue {
  @Prop({ required: true })
  currentPage: number

  @Prop({ required: true })
  fetchCallback: FetchCallback<any>

  @Prop({ required: true })
  perPage: number

  @Prop({ default: 'waveDots' })
  spinner: string

  @Prop({ default: () => ({}) })
  query: object

  @Prop({ default: 'section' })
  tag: string

  @Prop({ default: 1 })
  basePage: number

  @Prop({ default: true })
  prefetch: boolean

  @Prop({ default: true })
  infiniteEnabled: boolean

  pagination: Pagination | null = null

  // Make dummy page also for the current page to keep scroll-target place.
  pages: PageCollection<any> = new PageCollection([], this.basePage)

  get canAutoload(): boolean {
    return this.currentPage >= this.basePage
  }

  async getFetchPagePromise<T = unknown>(number: number): Promise<
    Either<Error, ListWithPagination<T>>
  > {
    /* Set dummy page */
    const loadingPage = new LoadingBlankPage(number, this.list(number))
    this.pages.add(loadingPage)

    return loadingPage.fetchPagePromise.then((listResult) => {
      if (listResult.isLeft()) {
        return listResult
      }

      /* Update the view state */
      const { list, pagination } = listResult.value
      this.pagination = pagination
      this.pages.add(new LoadedPage(
        number,
        pagination.exceedsLastPage ? [] : list
      ))

      return listResult
    })
  }

  /**
   * Load page data by specifying a page number.
   */
  async loadPage(
    number: number,
    $state: StateChanger | null = null
  ): Promise<void> {
    /* Check if the page number exists */
    const target = this.pages.find(number)

    if (target && target.loaded) {
      if (!$state) {
        return  // Skip
      }

      if (this.pagination && this.alreadyLastPageLoaded(this.pagination)) {
        $state.complete()
      } else {
        // infinite-loadingよりも下の要素(ページのフッターなど)を
        // 画面に表示してcurrentPageが1になっているときなどに、
        // 短期間でinfinite-loadingの$state.loadedとautoloadNextPageが
        // 交互に高速で呼ばれることを防ぐため、$state.loadedの発火を遅延させる
        const waitTime = 100
        setTimeout($state.loaded, waitTime)
      }

      return
    }

    const listResult = await ((): Promise<Either<Error, ListWithPagination<unknown>>> => {
      const p = target && target instanceof LoadingBlankPage
                ? target.fetchPagePromise
                : this.getFetchPagePromise(number)

      return p.catch(e => left(e))
    })()

    /* Error handling */
    if (listResult.isLeft()) {
      const onError = !$state ? undefined : () => {
        $state.loaded()
        $state.complete()
      }

      this.callbackOnError(listResult.value, onError)

      return
    }

    const callback = !$state ? undefined : {
      onNoItem: () => {
        $state.loaded()
        $state.complete()
      },
      onLoaded: () => {
        $state.loaded()
      },
      onEnd: () => {
        $state.complete()
      },
    }

    this.callbackOnLoad(listResult.value, callback)
  }

  /**
   * Load page data when autoload is triggered.
   */
  autoloadNextPage($state: StateChanger) {
    const nextPage = this.currentPage + 1
    this.loadPage(nextPage, $state)
  }

  @Watch('currentPage', { immediate: true })
  async onCurrentPageChange() {
    /* Fill in previous pages */
    this.pages.fillInPreviousPages(this.currentPage)

    /* Try to load current page in case there is no current page data */
    if (this.canAutoload) {
      await this.loadPage(this.currentPage)
    }

    /* Prefetch surrounding pages */
    if (this.prefetch && this.infiniteEnabled) {

      const surroundPages = [
        ... this.basePage <= this.currentPage ? [this.currentPage - 1] : [],
        this.currentPage + 1,
      ].filter(page => page >= this.basePage)

      await Promise.all(surroundPages.map(n => this.loadPage(n)))
    }
  }

  private async list(page: number): Promise<Either<Error, ListWithPagination<any>>> {
    if (page < this.basePage) {
      return new Left(new Error(`You are calling page ${page}, which is under MPA scope.`))
    }

    const query = {
      limit: this.perPage,
      // Load from the end of the previous page to the end of this page
      start: (page - 1) * this.perPage,
      ...this.query,
    }

    return await this.fetchCallback(
      query,
      {
        targetPage: page,
        basePage: this.basePage,
      }
    )
  }

  private alreadyLastPageLoaded(pagination: Pagination): boolean {
    const lastPageNumber = Math.ceil(pagination.total / this.perPage)
    if (lastPageNumber <= this.basePage) {
      return true
    }

    const lastPage = this.pages.find(lastPageNumber)

    return lastPage && lastPage.loaded || false
  }

  private callbackOnLoad<T = unknown>(
    { list, pagination }: ListWithPagination<T>,
    callback: {
      onNoItem?: (() => void),
      onLoaded?: (() => void),
      onEnd?: (() => void),
    } = {}
  ) {
    const { onNoItem, onLoaded, onEnd } = callback

    if (list.length === 0 && !pagination.hasNextPage) {
      this.complete({ pagination: this.pagination, pages: this.pages })

      if (onNoItem) {
        onNoItem()
      }

      return []
    }

    /* Loaded */
    if (onLoaded) {
      onLoaded()
    }

    /* End of page */
    if (pagination.isLastPage) {
      this.complete({ pagination: this.pagination, pages: this.pages })

      if (onEnd) {
        onEnd()
      }
    }
  }

  private callbackOnError(
    error: Error,
    onError?: (() => void)
  ) {
    if (error) {
      this.error({
        error,
        pagination: this.pagination,
        pages: this.pages,
      })

      if (onError) {
        onError()
      }

      return []
    }
  }

  @Emit()
  private complete(_result: AutoLoadComplete) {
    // nothing to do
  }

  @Emit()
  private error(_error: AutoLoadError) {
    // nothing to do
  }
}
